import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  orderBy, 
  where, 
  getDoc, 
  Timestamp, 
  serverTimestamp 
} from 'firebase/firestore';
import { db, auth } from './firebase';

// Collection name
const SUPPORT_COLLECTION = 'supportMessages';

/**
 * Creates a new support message from user to admin
 */
export const createSupportMessage = async (messageData) => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be logged in to send a support message');
    }

    const messageWithMetadata = {
      ...messageData,
      uid: user.uid,
      userEmail: user.email || messageData.email,
      userName: user.displayName || messageData.name,
      createdAt: serverTimestamp(),
      status: 'unread', // unread, read, replied
      replies: []
    };

    const docRef = await addDoc(collection(db, SUPPORT_COLLECTION), messageWithMetadata);
    return { id: docRef.id, ...messageWithMetadata };
  } catch (error) {
    console.error('Error creating support message:', error);
    throw error;
  }
};

/**
 * Gets all support messages for admin view
 */
export const getAllSupportMessages = async () => {
  try {
    const q = query(
      collection(db, SUPPORT_COLLECTION), 
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error getting support messages:', error);
    throw error;
  }
};

/**
 * Gets user's support messages
 */
export const getUserSupportMessages = async () => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be logged in to view support messages');
    }

    const q = query(
      collection(db, SUPPORT_COLLECTION),
      where('uid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error getting user support messages:', error);
    throw error;
  }
};

/**
 * Add admin reply to a support message
 */
export const addReplyToSupportMessage = async (messageId, replyText) => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Admin must be logged in to reply');
    }

    // Get the message document
    const messageRef = doc(db, SUPPORT_COLLECTION, messageId);
    const messageDoc = await getDoc(messageRef);
    
    if (!messageDoc.exists()) {
      throw new Error('Support message not found');
    }
    
    const messageData = messageDoc.data();
    
    // Create the reply object with a regular JavaScript Date object instead of serverTimestamp()
    const reply = {
      text: replyText,
      adminUid: user.uid,
      adminName: user.displayName || user.email,
      createdAt: new Date()  // Use regular Date object instead of serverTimestamp()
    };
    
    // Add the reply to the message's replies array
    const updatedReplies = [...(messageData.replies || []), reply];
    
    // Update the message document
    await updateDoc(messageRef, {
      replies: updatedReplies,
      status: 'replied',
      lastRepliedAt: serverTimestamp()  // serverTimestamp() is fine here as it's not in an array
    });
    
    return {
      id: messageId,
      ...messageData,
      replies: updatedReplies,
      status: 'replied'
    };
  } catch (error) {
    console.error('Error adding reply to support message:', error);
    throw error;
  }
};

/**
 * Update message status (read/unread)
 */
export const updateMessageStatus = async (messageId, status) => {
  try {
    const messageRef = doc(db, SUPPORT_COLLECTION, messageId);
    await updateDoc(messageRef, { status });
    return { id: messageId, status };
  } catch (error) {
    console.error('Error updating message status:', error);
    throw error;
  }
}; 